'use client'

import dynamic from 'next/dynamic'
import React from 'react'

import { FormioSkeleton } from './FormioSkeleton'
import { SuccessMessage } from './SuccessMessage'

import type { Webform } from '@formio/js'
import type { FormProps, FormSource } from '@formio/react'
// Load Form component client-side only
const Form = dynamic(() => import('@formio/react').then((mod) => mod.Form), { ssr: false })

// Load Provider client-side only
const FormioProvider = dynamic(() => import('@formio/react').then((mod) => mod.FormioProvider), { ssr: false })

interface FormioWrapperProps extends FormProps {
  onFormReady?: (formInstance: Webform) => void
  setIsDataEntered?: (isDataEntered: boolean) => void
  setRenderConfirm?: (renderConfirm: boolean) => void
  type?: 'page' | 'drawer'
  onSubmitForm?: (val: boolean) => void
  showSuccess?: boolean
}

// List of all the form types and their corresponding skeleton rows
export const FORM_SKELETON_ROWS: Record<string, number> = {
  partialonboardingflowtype1: 9,
  partialonboardingflowtype2: 9,
  partialonboardingflowtype3: 6,
  partialonboardingflowtype4: 8,
  heateronboarding: 3,
  contactus: 5,
  immoving: 5,
  headerenquire: 15,
  promotionenquire: 13,
}

// Get the number of skeleton rows based on the form src
export const getSkeletonRows = (src?: string) => {
  if (!src) return 3

  const entry = Object.entries(FORM_SKELETON_ROWS).find(([key]) => src.includes(key))

  return entry?.[1] ?? 3
}

const FormioWrapper = ({ onFormReady, setIsDataEntered, setRenderConfirm, onSubmitForm, showSuccess = true, type = 'page', ...props }: FormioWrapperProps) => {
  const { src, form, submission, options, onError } = props
  const skeletonRows = getSkeletonRows(src as string)
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const projectUrl = process.env.NEXT_PUBLIC_FORM_IO_PROJECT_URL

  return (
    <div className="w-full" data-type={type}>
      {isLoading && <FormioSkeleton rows={skeletonRows} />}
      {!showSuccessMessage ? (
        <FormioProvider projectUrl={projectUrl}>
          <Form
            src={src as FormSource}
            form={form}
            submission={submission ?? undefined}
            options={{
              readOnly: false,
              noAlerts: false,
              ...options,
            }}
            formReady={(formInstance: Webform) => {
              setIsLoading(false)
              formInstance.on('submitDone', () => {
                formInstance.disabled = true
              })
              onFormReady?.(formInstance)

              formInstance.on('change', () => {
                const errors = formInstance.validate()
                const allValid = errors.length === 0
                setIsDataEntered?.(allValid)

                const triggered = (formInstance.data as { getQuoteTriggered?: boolean })?.getQuoteTriggered === true
                setRenderConfirm?.(triggered)
              })
            }}
            onSubmitError={onError}
            onSubmitDone={(submission) => {
              if (submission.state === 'submitted') {
                setShowSuccessMessage(showSuccess)
                onSubmitForm?.(true)
              }
            }}
          />
        </FormioProvider>
      ) : (
        <SuccessMessage type={type} />
      )}
    </div>
  )
}

export default FormioWrapper
